// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { hasChanges } from "@insite/client-framework/Store/Pages/AccountSettings/AccountSettingsSelectors";
import saveCurrentAccount from "@insite/client-framework/Store/Pages/AccountSettings/Handlers/SaveCurrentAccount";
import setInitialValues from "@insite/client-framework/Store/Pages/AccountSettings/Handlers/SetInitialValues";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { HiddenProps } from "@insite/mobius/Hidden";
import { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import xLoadingOverlay from "@tps/App/Handlers/XNG/LoadingOverlay";
import { xRegex } from "@tps/XDL/Utilities/validation";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    account: state.pages.accountSettings.editingAccount,
    isEmailValid: !state.pages.accountSettings.emailErrorMessage,
    hasChanges: hasChanges(state),
    useDefaultCustomer: state.pages.accountSettings.useDefaultCustomer,
    initialUseDefaultCustomer: state.pages.accountSettings.initialUseDefaultCustomer,
    pageTitle: getCurrentPage(state).fields.title,
});

const mapDispatchToProps = {
    saveCurrentAccount,
    setInitialValues,
};

// XNG-Changes
const enum fields {
    showActionButtonsOnly = "showActionButtonsOnly",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showActionButtonsOnly]: boolean;
    };
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface AccountSettingsHeaderStyles {
    title: TypographyProps;
    titleWrapper: GridItemProps;
    buttonSetHidden?: HiddenProps;
    saveButton?: ButtonPresentationProps;
    cancelButton?: ButtonPresentationProps;
    menuHidden?: HiddenProps;
    overflowMenu?: OverflowMenuPresentationProps;
    saveClickable?: ClickablePresentationProps;
    cancelClickable?: ClickablePresentationProps;
    buttonSet?: GridItemProps;
}

export const headerStyles: AccountSettingsHeaderStyles = {
    saveButton: {
        css: css`
            margin-left: 10px;
        `,
    },
    buttonSetHidden: {
        below: "md",
    },
    menuHidden: {
        above: "sm",
    },
    buttonSet: {
        css: css`
            justify-content: flex-end;
        `,
        width: [1, 1, 4, 3, 3],
    },
    titleWrapper: {
        width: [11, 11, 8, 9, 9],
    },
    title: {
        variant: "h1",
    },
};

const styles = headerStyles;

const AccountSettingsHeader = ({
    account,
    isEmailValid,
    hasChanges,
    pageTitle,
    useDefaultCustomer,
    saveCurrentAccount,
    setInitialValues,
    fields: { showActionButtonsOnly },
}: Props) => {
    if (!account) {
        return null;
    }

    // XNG-Change TPS-779
    const [invalidEmailAddress, setInvalidEmailAddress] = useState(false);
    useEffect(() => {
        xRegex.email.test(account.email) ? setInvalidEmailAddress(false) : setInvalidEmailAddress(true);
    }, [account.email]);

    const dispatch = useDispatch();

    const toasterContext = useContext(ToasterContext);
    const updateSettingsHandler = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        event.preventDefault();
        saveCurrentAccount({
            onSuccess: () => {
                toasterContext.addToast({ body: translate("Settings updated"), messageType: "success" });
            },
            onError: message => {
                toasterContext.addToast({ body: message, messageType: "danger" });
                dispatch(xLoadingOverlay({ key: "clear", id: "save_current_account" }));
            },
        });
    };

    const invalidCustomerState = useDefaultCustomer && !account.defaultCustomerId;
    const enableSaveButton = hasChanges && isEmailValid && !invalidCustomerState;

    return (
        <GridContainer>
            {!showActionButtonsOnly ? (
                <GridItem {...styles.titleWrapper}>
                    <Typography {...styles.title}>{pageTitle}</Typography>
                </GridItem>
            ) : (
                <GridItem {...styles.buttonSet}>
                    {hasChanges && (
                        <Button {...styles.cancelButton} onClick={setInitialValues} aria-label="Cancel">
                            {translate("Cancel")}
                        </Button>
                    )}
                    <Button
                        {...styles.saveButton}
                        onClick={updateSettingsHandler}
                        // XNG-Change TPS-779
                        disabled={!enableSaveButton || invalidEmailAddress}
                        data-test-selector="accountSettings_save"
                        aria-label="Save"
                    >
                        {translate("Save")}
                    </Button>
                    {/* <Hidden {...styles.menuHidden}>
                    <OverflowMenu position="end" {...styles.overflowMenu}>
                        <Clickable {...styles.cancelClickable} aria-label={translate("Cancel")} onClick={setInitialValues} disabled={!hasChanges}>
                            {translate("Cancel")}
                        </Clickable>
                        <Clickable
                            {...styles.saveClickable}
                            onClick={updateSettingsHandler}
                            disabled={!enableSaveButton}
                            aria-label={translate("Save")}
                        >
                            {translate("Save")}
                        </Clickable>
                    </OverflowMenu>
                </Hidden> */}
                </GridItem>
            )}
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AccountSettingsHeader),
    definition: {
        group: "Account Settings",
        allowedContexts: ["AccountSettingsPage"],
        displayName: "Header",
        // XNG-Changes
        fieldDefinitions: [
            {
                name: fields.showActionButtonsOnly,
                displayName: "Show Action Buttons Only",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
